import { useEffect, useState } from 'react'

import { Input, Modal, Spin } from 'antd'

import { INITIAL_LOCKOUT, isLockedOut, nextLockoutState, type LockoutState } from './pin-lockout'
import styles from './pin-prompt-modal.module.scss'

type PinPromptModalProps = {
  isVisible: boolean
  emrPin: string
  onCancel?: () => void
  onVerified: () => void
  dismissible?: boolean
  loading?: boolean
  zIndex?: number
}

const onlyDigits = (value: string): string => value.replace(/\D/g, '')

export const PinPromptModal = ({
  isVisible,
  emrPin,
  onCancel,
  onVerified,
  dismissible = true,
  loading = false,
  zIndex,
}: PinPromptModalProps) => {
  const pinLength = emrPin.length
  const [attemptKey, setAttemptKey] = useState(0)
  const [lockout, setLockout] = useState<LockoutState>(INITIAL_LOCKOUT)
  const [error, setError] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  const locked = isLockedOut(lockout, now)
  const remainingSeconds = lockout.lockedUntil
    ? Math.max(0, Math.ceil((lockout.lockedUntil - now) / 1000))
    : 0

  useEffect(() => {
    if (!isVisible) {
      setError(false)
      setAttemptKey((key) => key + 1)
    }
  }, [isVisible])

  useEffect(() => {
    if (lockout.lockedUntil === null) return
    const remainingMs = lockout.lockedUntil - Date.now()
    if (remainingMs <= 0) return
    const timerId = setTimeout(() => setNow(Date.now()), remainingMs)
    return () => clearTimeout(timerId)
  }, [lockout.lockedUntil])

  const handleComplete = (raw: string) => {
    if (locked) return
    const entered = onlyDigits(raw)
    if (emrPin.length === 0 || entered.length < emrPin.length) return
    if (entered === emrPin) {
      onVerified()
      return
    }
    setError(true)
    setLockout((prev) => nextLockoutState(prev, Date.now()))
    setNow(Date.now())
    setAttemptKey((key) => key + 1)
  }

  return (
    <Modal
      open={isVisible}
      onCancel={dismissible ? onCancel : undefined}
      maskClosable={false}
      keyboard={dismissible}
      closable={dismissible}
      footer={null}
      centered
      width={440}
      zIndex={zIndex}
      title={null}
      classNames={{ wrapper: styles.wrapper }}>
      <div className={styles.body}>
        <h2 className={styles.title}>Profile PIN</h2>
        <p className={styles.hint}>Enter the profile PIN to close the intake forms.</p>
        {loading ? (
          <div className={styles.loading}>
            <Spin size='large' />
          </div>
        ) : (
          pinLength > 0 && (
            <div className={styles.otp}>
              <Input.OTP
                key={attemptKey}
                length={pinLength}
                onChange={handleComplete}
                disabled={locked}
                mask='●'
                size='large'
                inputMode='numeric'
                formatter={onlyDigits}
              />
            </div>
          )
        )}
        {error && !locked && (
          <p className={styles.error} role='alert'>
            Incorrect PIN. Please try again.
          </p>
        )}
        {locked && (
          <p className={styles.error} role='alert'>
            Too many attempts. Try again in {remainingSeconds}s.
          </p>
        )}
        {dismissible && (
          <button type='button' className={styles.cancel} onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </Modal>
  )
}
